import React from "react";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ReferenceLine,
  ScatterChart,
  Scatter,
  Cell,
  ResponsiveContainer,
  Tooltip,
  Legend,
} from "recharts";





const GraphTitle: React.FC<{ title: string }> = ({ title }) => (
  <div
    style={{
      background: "#f8d0d0",
      textAlign: "center",
      fontWeight: "bold",
      fontSize: 14,
      padding: "6px 0",
      borderBottom: "1px solid #ccc",
    }}
  >
    {title}
  </div>
);

const ZScoreRow: React.FC<{ value: string | number }> = ({ value }) => {
  const valueText =
    typeof value === "number" && Number.isFinite(value) ? value.toFixed(2) : String(value ?? "");

  return (
  <table
    style={{
      width: "100%",
      borderCollapse: "collapse",
      marginBottom: 4,
      fontSize: 13,
    }}
  >
    <tbody>
      <tr>
        <td
          style={{
            border: "1px solid #999",
            padding: "4px 12px",
            fontWeight: "bold",
            width: "50%",
            textAlign: "left",
          }}
        >
          Z-score
        </td>
        <td
          style={{
            border: "1px solid #999",
            padding: "4px 12px",
            width: "50%",
            textAlign: "right",
          }}
        >
          {valueText}
        </td>
      </tr>
    </tbody>
  </table>
  );
};

// Bar graph

const ChromosomeBarChart: React.FC = ({ chromosomTableData }) => {
  return (
    <div
      style={{
        border: "1px solid #ccc",
        borderRadius: 4,
        overflow: "hidden",
      }}
    >
      <GraphTitle title="Chromosome 1 - 22" />
      <div style={{ padding: "8px 4px 0 4px" }}>
        <ResponsiveContainer width="100%" height={260}>
          <BarChart
            data={chromosomTableData}
            margin={{ top: 10, right: 10, left: 0, bottom: 5 }}
          >
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
            dataKey="name"
            tick={{ fontSize: 10 }}
            interval={0}
            tickFormatter={(value) => {
                const match = value.match(/\d+/);
                return match ? match[0] : value;
              }}
          />
          <YAxis
            domain={[-16, 16]}
            ticks={[-16, -12, -8, -4, 0, 4, 8, 12, 16]}
            tick={{ fontSize: 10 }}
          />
          <ReferenceLine y={0} stroke="#000" />
          <Bar dataKey="score" fill="#999" barSize={18} />
        </BarChart>
      </ResponsiveContainer>
    </div>
  </div>
  );
};

// scatter graph

interface ScatterGraphProps {
  title: string;
  zScore: string | number;
  data: {
    wt: { x: number; y: number }[];
    trisomy: { x: number; y: number }[];
    sample: { x: number; y: number }[];
    trisomyLabel: string;
  };
}

const ChromosomeScatterChart: React.FC<ScatterGraphProps> = ({
  title,
  zScore,
  data,
}) => (
  <div
    style={{
      border: "1px solid #ccc",
      borderRadius: 4,
      overflow: "hidden",
    }}
  >
    <GraphTitle title={title} />
    <ZScoreRow value={zScore} />
    <div style={{ padding: "0 4px" }}>
      <ResponsiveContainer width="100%" height={230}>
        <ScatterChart margin={{ top: 10, right: 10, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" />
          <XAxis
              dataKey="x"
              type="number"
              domain={[0, "dataMax"]}
              tick={{ fontSize: 9 }}
/>
          <YAxis
            dataKey="y"
            type="number"
            domain={[-16, 16]}
            ticks={[-16, -12, -8, -4, 0, 4, 8, 12, 16]}
            tick={{ fontSize: 9 }}
          />
          <ReferenceLine
            y={-3}
            stroke="#000"
            strokeDasharray="6 4"
            strokeWidth={1.5}
          />
          <Legend
            wrapperStyle={{ fontSize: 11, paddingTop: 0 }}
            iconSize={10}
          />
          {/* WT – blue open circles */}
          <Scatter
            name="WT"
            data={data.wt}
            fill="none"
            stroke="#2196F3"
            strokeWidth={1.5}
            shape="circle"
          />
          {/* Trisomy – red filled circles */}
          <Scatter
            name={data.trisomyLabel}
            data={data.trisomy}
            fill="#e53935"
            shape="circle"
          />
          {/* Current sample – black filled circle, larger */}
          <Scatter name="sample" data={data.sample} fill="#000" shape="circle">
            {data.sample.map((_, idx) => (
              <Cell key={idx} r={6} />
            ))}
          </Scatter>
        </ScatterChart>
      </ResponsiveContainer>
    </div>
  </div>
);

// main component
const ChromosomeGraphs: React.FC = ({ chromosomTableData, niptGraphData, tableData, currentBookingId }) => {
  
  const getTableZScoreRaw = (nameKeyword: string) => {
    const row = (tableData || []).find((t: any) =>
      (t?.name || "").toLowerCase().includes(nameKeyword.toLowerCase())
    );
    return String(row?.score ?? "").trim();
  };

  const getTableZScoreNumber = (nameKeyword: string) => {
    const raw = getTableZScoreRaw(nameKeyword);
    const y = parseFloat(raw);
    return isNaN(y) ? 0 : y;
  };

  const buildScatterData = (trisomyLabel: string, nameKeyword: string) => {
    const entries = niptGraphData?.data || [];

    const wt: { x: number; y: number }[] = [];
    const trisomy: { x: number; y: number }[] = [];
    const sample: { x: number; y: number }[] = [];

    entries.forEach((entry: any, index: number) => {
      const x = index + 1; // ✅ use index instead of booking_id

      const match = entry.test_result_nipt?.find((t: any) =>
        t.name.toLowerCase().includes(nameKeyword.toLowerCase())
      );

      if (!match) return;

      const y = parseFloat(match.score);
      if (isNaN(y)) return;

      const risk = String(match?.risk ?? "").toLowerCase().trim();
      if (risk === "red") {
        trisomy.push({ x, y });
      } else {
        wt.push({ x, y });
      }
    });

    const tableY = getTableZScoreNumber(nameKeyword);
    if (entries.length > 0) {
      const x = entries.length;
      sample.push({ x, y: tableY });
    }

    return { wt, trisomy, sample, trisomyLabel };
  };

  const chr13Data = buildScatterData("T13", "trisomy 13");
  const chr18Data = buildScatterData("T18", "trisomy 18");
  const chr21Data = buildScatterData("T21", "trisomy 21");

  const getZScore = (nameKeyword: string) => {
    const raw = getTableZScoreRaw(nameKeyword);
    const n = parseFloat(raw);
    return raw !== "" ? raw : Number.isFinite(n) ? n.toFixed(2) : 0;
  };

  return (
    <div
      className="chromosome-graphs-page"
      style={{
        width: "100%",
        padding: "0px 0px",
        boxSizing: "border-box",
        background: "#fff",
      }}
    >
      <div
        style={{
          display: "grid",
          gridTemplateColumns: "1fr 1fr",
          gridTemplateRows: "auto auto",
          gap: 20,
        }}
      >
       
        <ChromosomeBarChart  chromosomTableData={chromosomTableData}/>

       
        <ChromosomeScatterChart

          title="Chromosome 13"
          zScore={getZScore("trisomy 13")}
          data={chr13Data}
        />

       
        <ChromosomeScatterChart
          title="Chromosome 18"
          zScore={getZScore("trisomy 18")}
          data={chr18Data}
        />

        
        <ChromosomeScatterChart
          title="Chromosome 21"
          zScore={getZScore("trisomy 21")}
          data={chr21Data}
        />
      </div>
    </div>
  );
};

export default ChromosomeGraphs;
